import { useState } from 'react';
import { Bell, XCircle } from 'lucide-react';
import type { Session, Business } from '@/types';

interface QrDisplayProps {
  session: Session;
  business: Business | null;
  onCancel?: () => void;
}

export function QrDisplay({ session, business, onCancel }: QrDisplayProps) {
  const [notifStatus, setNotifStatus] = useState<string>('default');

  const handleNotify = async () => {
    if (!('Notification' in window)) {
      setNotifStatus('unsupported');
      return;
    }
    const permission = await Notification.requestPermission();
    setNotifStatus(permission);
  };

  const waitMinutes = session.estimated_wait
    ? Math.round(session.estimated_wait / 60)
    : null;

  return (
    <div className="flex flex-col items-center gap-6 p-6 w-full max-w-md mx-auto animate-fade-in">
      {business?.logo_url && (
        <img
          src={business.logo_url}
          alt={business.name}
          className="h-12 max-h-12 object-contain"
        />
      )}

      <div
        className="flex flex-col items-center gap-4 w-full p-6 rounded-2xl"
        style={{
          background: 'var(--qr-surface)',
          border: '1px solid var(--qr-border)',
        }}
      >
        <span
          className="font-mono font-extrabold tracking-wider"
          style={{ fontSize: '72px', lineHeight: 1, color: 'var(--qr-text)' }}
        >
          {session.public_id}
        </span>

        {session.qr_url ? (
          <img
            src={session.qr_url}
            alt="Código QR"
            className="w-56 h-56 object-contain rounded-lg"
          />
        ) : (
          <div
            className="w-56 h-56 rounded-lg flex items-center justify-center"
            style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)' }}
          >
            <span className="text-sm text-[var(--qr-text-secondary)]">QR no disponible</span>
          </div>
        )}

        {typeof session.position === 'number' && session.status === 'waiting' && (
          <div className="text-center">
            <p className="text-lg font-medium" style={{ color: 'var(--qr-text-secondary)' }}>
              Eres el
            </p>
            <p
              className="text-5xl font-extrabold my-1"
              style={{ color: 'var(--qr-primary)' }}
            >
              Nº {session.position + 1}
            </p>
            {waitMinutes !== null && (
              <p className="text-base" style={{ color: 'var(--qr-text-secondary)' }}>
                ~{waitMinutes} min estimados
              </p>
            )}
          </div>
        )}

        {session.status === 'notified' && (
          <div className="text-center px-4 py-3 rounded-xl w-full" style={{ background: 'var(--qr-success)', color: '#fff' }}>
            <p className="text-xl font-bold">¡Tu mesa está lista!</p>
            {session.table_number && (
              <p className="text-lg mt-1">Mesa {session.table_number}</p>
            )}
          </div>
        )}

        {session.status === 'seated' && (
          <div className="text-center px-4 py-3 rounded-xl w-full" style={{ background: 'var(--qr-accent)', color: '#fff' }}>
            <p className="text-lg font-semibold">Disfruta tu comida</p>
          </div>
        )}

        {session.status === 'cancelled' && (
          <div className="text-center px-4 py-3 rounded-xl w-full" style={{ background: 'var(--qr-danger)', color: '#fff' }}>
            <p className="text-lg font-semibold">Turno cancelado</p>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 w-full">
        {'Notification' in window && notifStatus !== 'granted' && (
          <button
            onClick={handleNotify}
            className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl font-semibold transition-transform duration-200 active:scale-[0.98]"
            style={{ background: 'var(--qr-primary)', color: '#fff' }}
          >
            <Bell size={18} />
            Permitir notificaciones
          </button>
        )}

        {onCancel && session.status === 'waiting' && (
          <button
            onClick={onCancel}
            className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl font-semibold transition-transform duration-200 active:scale-[0.98]"
            style={{
              background: 'transparent',
              border: '1px solid var(--qr-danger)',
              color: 'var(--qr-danger)',
            }}
          >
            <XCircle size={18} />
            Cancelar mi turno
          </button>
        )}
      </div>
    </div>
  );
}
